import { useCallback, useRef } from 'react';
import type {
  ChatbotErrorEvent,
  ChatbotFileEvent,
  ChatbotRateLimit,
  ChatbotReasoningEvent,
  ChatbotSSEEvent,
  ChatbotTextEvent,
} from '@/models/chatbot';
import { CHATBOT_DONE_MARKER } from '@/models/chatbot';

const SSE_BLOCK_SEPARATOR = /\r?\n\r?\n/;

interface StreamCallbacks {
  onTextChunk: (text: string) => void;
  onFileEvent: (file: ChatbotFileEvent) => void;
  onReasoningChunk: (text: string) => void;
  onError: (error: string) => void;
  onDone: () => void;
  onRateLimit: (rateLimit: Partial<ChatbotRateLimit>) => void;
}

class StreamError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'StreamError';
    this.status = status;
  }
}

function isStreamError(value: unknown): value is StreamError {
  return value instanceof StreamError;
}

function isReasoningEvent(event: ChatbotSSEEvent): event is ChatbotReasoningEvent {
  return 'type' in event && event.type === 'reasoning';
}

function isTextEvent(event: ChatbotSSEEvent): event is ChatbotTextEvent {
  return 'text' in event && !isReasoningEvent(event);
}

function isFileEvent(event: ChatbotSSEEvent): event is ChatbotFileEvent {
  return 'type' in event && event.type === 'file';
}

function isErrorEvent(event: ChatbotSSEEvent): event is ChatbotErrorEvent {
  return 'error' in event;
}

function extractRateLimitHeaders(headers: Headers): Partial<ChatbotRateLimit> {
  const remaining = headers.get('X-RateLimit-Remaining');
  const limit = headers.get('X-RateLimit-Limit');
  const resetAt = headers.get('X-RateLimit-Reset');

  return {
    ...(remaining !== null ? { remaining: parseInt(remaining, 10) } : {}),
    ...(limit !== null ? { limit: parseInt(limit, 10) } : {}),
    ...(resetAt !== null ? { resetAt: parseInt(resetAt, 10) } : {}),
  };
}

function parseSSEBlock(block: string): string | null {
  const dataLines = block
    .split(/\r?\n/)
    .filter(line => line.startsWith('data:'))
    .map(line => line.slice(5).replace(/^ /, ''));

  return dataLines.length > 0 ? dataLines.join('\n') : null;
}

function dispatchSSEEvent(event: ChatbotSSEEvent, callbacks: StreamCallbacks): void {
  if (isReasoningEvent(event)) {
    callbacks.onReasoningChunk(event.text);
  } else if (isTextEvent(event)) {
    callbacks.onTextChunk(event.text);
  } else if (isFileEvent(event)) {
    callbacks.onFileEvent(event);
  } else if (isErrorEvent(event)) {
    callbacks.onError(event.error);
  }
}

interface UseChatbotStreamReturn {
  startStream: (streamUrl: string, callbacks: StreamCallbacks) => Promise<void>;
  abortStream: () => void;
}

const useChatbotStream = (): UseChatbotStreamReturn => {
  const abortControllerRef = useRef<AbortController | null>(null);

  const startStream = useCallback(async (streamUrl: string, callbacks: StreamCallbacks): Promise<void> => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const response = await fetch(streamUrl, { signal: controller.signal });

    const rateLimit = extractRateLimitHeaders(response.headers);
    if (Object.keys(rateLimit).length > 0) {
      callbacks.onRateLimit(rateLimit);
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage =
        typeof body === 'object' && body !== null && 'error' in body
          ? String(body.error)
          : `Stream request failed with status ${response.status}`;
      throw new StreamError(response.status, errorMessage);
    }

    if (!response.body) {
      throw new StreamError(0, 'Response body is not readable');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split(SSE_BLOCK_SEPARATOR);
        buffer = blocks.pop() ?? '';

        for (const block of blocks) {
          const line = parseSSEBlock(block);
          if (!line) continue;

          if (line === CHATBOT_DONE_MARKER) {
            callbacks.onDone();
            return;
          }

          const event: ChatbotSSEEvent = JSON.parse(line);
          dispatchSSEEvent(event, callbacks);
        }
      }

      if (buffer.trim()) {
        const line = parseSSEBlock(buffer);
        if (line && line !== CHATBOT_DONE_MARKER) {
          const event: ChatbotSSEEvent = JSON.parse(line);
          dispatchSSEEvent(event, callbacks);
        }
      }

      callbacks.onDone();
    } finally {
      reader.releaseLock();
    }
  }, []);

  const abortStream = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  return { startStream, abortStream };
};

export { isStreamError };
export type { StreamError, StreamCallbacks };
export default useChatbotStream;
